import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CalendarDays,
  CheckCircle2,
  CircleX,
  Clock3,
  GraduationCap,
  Save,
  UserRound
} from 'lucide-react';

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch
} from 'firebase/firestore';

import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';
import TeacherShell from './TeacherShell';

function todayLocal() {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(
    now.getMonth() + 1
  ).padStart(2, '0');

  const day = String(
    now.getDate()
  ).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function studentName(student) {
  return (
    student.fullName ||
    student.fullname ||
    student.name ||
    'Élève'
  );
}

function safeId(value) {
  return String(value || '')
    .replaceAll('/', '_')
    .replaceAll(' ', '_');
}

export default function TeacherClassPage() {
  const navigate = useNavigate();
  const { classId } = useParams();
  const { user, profile } = useAuth();

  const [classInfo, setClassInfo] =
    useState(null);

  const [subjects, setSubjects] =
    useState([]);

  const [students, setStudents] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  // ---------------------------------------------
  // PRÉSENCES
  // ---------------------------------------------

  const [attendanceDate, setAttendanceDate] =
    useState(todayLocal());

  const [
    selectedSubjectId,
    setSelectedSubjectId
  ] = useState('');

  const [
    attendance,
    setAttendance
  ] = useState({});

  const [
    existingAttendanceIds,
    setExistingAttendanceIds
  ] = useState(new Set());

  const [
    loadingAttendance,
    setLoadingAttendance
  ] = useState(false);

  const [
    savingAttendance,
    setSavingAttendance
  ] = useState(false);

  const [
    attendanceError,
    setAttendanceError
  ] = useState('');

  const [
    attendanceSuccess,
    setAttendanceSuccess
  ] = useState('');

  const teacherName =
    profile?.fullname ||
    profile?.fullName ||
    profile?.displayName ||
    'Enseignant';

  const className =
    classInfo?.name ||
    classInfo?.className ||
    classId;

  const selectedSubject =
    useMemo(
      () =>
        subjects.find(
          (subject) =>
            subject.id ===
            selectedSubjectId
        ) || null,
      [
        subjects,
        selectedSubjectId
      ]
    );

  // =============================================
  // CHARGEMENT DE LA CLASSE
  // =============================================

  useEffect(() => {
    async function loadClass() {
      if (!user?.uid || !classId) {
        return;
      }

      setLoading(true);
      setError('');

      try {
        // 1. Vérifier les affectations.
        const assignmentsQuery = query(
          collection(
            db,
            'teacherAssignments'
          ),
          where(
            'teacherId',
            '==',
            user.uid
          )
        );

        const assignmentSnapshot =
          await getDocs(
            assignmentsQuery
          );

        const classAssignments =
          assignmentSnapshot.docs
            .map((assignmentDoc) => ({
              id: assignmentDoc.id,
              ...assignmentDoc.data()
            }))
            .filter(
              (assignment) =>
                assignment.classId ===
                  classId &&
                assignment.status ===
                  'active'
            );

        if (
          classAssignments.length === 0
        ) {
          setError(
            'Cette classe ne fait pas partie de vos affectations.'
          );

          return;
        }

        // 2. Informations de la classe.
        const classSnapshot =
          await getDoc(
            doc(
              db,
              'classes',
              classId
            )
          );

        if (
          !classSnapshot.exists()
        ) {
          setError(
            'Classe introuvable.'
          );

          return;
        }

        setClassInfo({
          id: classSnapshot.id,
          ...classSnapshot.data()
        });

        // 3. Matières affectées à cet enseignant.
        const loadedSubjects = [];

        for (
          const assignment
          of classAssignments
        ) {
          if (
            !assignment.subjectId
          ) {
            continue;
          }

          const subjectSnapshot =
            await getDoc(
              doc(
                db,
                'subjects',
                assignment.subjectId
              )
            );

          loadedSubjects.push({
            id:
              assignment.subjectId,

            name:
              subjectSnapshot.exists()
                ? (
                    subjectSnapshot
                      .data()
                      .name ||
                    subjectSnapshot
                      .data()
                      .subjectName ||
                    assignment.subjectId
                  )
                : assignment.subjectId
          });
        }

        setSubjects(
          loadedSubjects
        );

        setSelectedSubjectId(
          (current) =>
            current ||
            loadedSubjects[0]?.id ||
            ''
        );

        // 4. Liste des élèves.
        const studentsSnapshot =
          await getDocs(
            collection(
              db,
              'classRosters',
              classId,
              'students'
            )
          );

        const loadedStudents =
          studentsSnapshot.docs
            .map((studentDoc) => ({
              id: studentDoc.id,
              ...studentDoc.data()
            }))
            .filter(
              (student) =>
                student.status !==
                'inactive'
            )
            .sort((a, b) =>
              studentName(a)
                .localeCompare(
                  studentName(b),
                  'fr'
                )
            );

        setStudents(
          loadedStudents
        );
      } catch (loadError) {
        console.error(
          'Erreur chargement classe enseignant :',
          loadError
        );

        setError(
          'Impossible de charger cette classe pour le moment.'
        );
      } finally {
        setLoading(false);
      }
    }

    loadClass();
  }, [
    user?.uid,
    classId
  ]);

  // =============================================
  // CHARGER LES PRÉSENCES EXISTANTES
  // =============================================

  useEffect(() => {
    async function loadAttendance() {
      if (
        !user?.uid ||
        !classId ||
        !selectedSubjectId ||
        students.length === 0
      ) {
        return;
      }

      setLoadingAttendance(true);
      setAttendanceError('');
      setAttendanceSuccess('');

      try {
        const attendanceQuery =
          query(
            collection(
              db,
              'attendance'
            ),
            where(
              'teacherId',
              '==',
              user.uid
            ),
            where(
              'classId',
              '==',
              classId
            )
          );

        const snapshot =
          await getDocs(
            attendanceQuery
          );

        const rows =
          snapshot.docs
            .map(
              (attendanceDoc) => ({
                id:
                  attendanceDoc.id,
                ...attendanceDoc.data()
              })
            )
            .filter(
              (item) =>
                item.date ===
                  attendanceDate &&
                item.subjectId ===
                  selectedSubjectId
            );

        const rowByStudent =
          new Map(
            rows.map(
              (item) => [
                item.studentId,
                item
              ]
            )
          );

        const nextAttendance =
          {};

        students.forEach(
          (student) => {
            const saved =
              rowByStudent.get(
                student.id
              );

            nextAttendance[
              student.id
            ] = {
              status:
                saved?.status ||
                'present',

              lateMinutes:
                saved?.lateMinutes
                  ? String(
                      saved.lateMinutes
                    )
                  : '',

              note:
                saved?.note ||
                ''
            };
          }
        );

        setAttendance(
          nextAttendance
        );

        setExistingAttendanceIds(
          new Set(
            rows.map(
              (item) => item.id
            )
          )
        );
      } catch (loadError) {
        console.error(
          'Erreur chargement présences :',
          loadError
        );

        setAttendanceError(
          'Impossible de charger les présences enregistrées.'
        );
      } finally {
        setLoadingAttendance(false);
      }
    }

    loadAttendance();
  }, [
    user?.uid,
    classId,
    selectedSubjectId,
    attendanceDate,
    students
  ]);

  // =============================================
  // MODIFICATION D'UN ÉLÈVE
  // =============================================

  function changeStatus(
    studentId,
    status
  ) {
    setAttendance(
      (current) => ({
        ...current,

        [studentId]: {
          status,

          lateMinutes:
            status === 'late'
              ? (
                  current[
                    studentId
                  ]?.lateMinutes ||
                  ''
                )
              : '',

          note:
            status === 'present'
              ? ''
              : (
                  current[
                    studentId
                  ]?.note ||
                  ''
                )
        }
      })
    );

    setAttendanceSuccess('');
  }

  function updateStudentAttendance(
    studentId,
    field,
    value
  ) {
    setAttendance(
      (current) => ({
        ...current,

        [studentId]: {
          ...(current[
            studentId
          ] || {
            status:
              'present',
            lateMinutes:
              '',
            note:
              ''
          }),

          [field]: value
        }
      })
    );

    setAttendanceSuccess('');
  }

  // =============================================
  // ENREGISTRER LA CLASSE
  // =============================================

  async function saveAttendance() {
    if (
      !user?.uid ||
      !selectedSubject
    ) {
      setAttendanceError(
        'Sélectionnez une matière.'
      );

      return;
    }

    for (
      const student
      of students
    ) {
      const value =
        attendance[
          student.id
        ];

      if (
        value?.status ===
          'late' &&
        (
          !value.lateMinutes ||
          Number(
            value.lateMinutes
          ) <= 0
        )
      ) {
        setAttendanceError(
          `Indiquez le retard de ${studentName(student)} en minutes.`
        );

        return;
      }
    }

    setSavingAttendance(true);
    setAttendanceError('');
    setAttendanceSuccess('');

    try {
      const batch =
        writeBatch(db);

      students.forEach(
        (student) => {
          const value =
            attendance[
              student.id
            ] || {
              status:
                'present',
              lateMinutes:
                '',
              note:
                ''
            };

          const attendanceId =
            [
              attendanceDate,
              safeId(classId),
              safeId(
                selectedSubjectId
              ),
              safeId(user.uid),
              safeId(student.id)
            ].join('__');

          const attendanceRef =
            doc(
              db,
              'attendance',
              attendanceId
            );

          const payload = {
            studentId:
              student.id,

            studentName:
              studentName(
                student
              ),

            classId,

            className,

            subjectId:
              selectedSubject.id,

            subjectName:
              selectedSubject.name,

            teacherId:
              user.uid,

            teacherName,

            date:
              attendanceDate,

            status:
              value.status ||
              'present',

            lateMinutes:
              value.status ===
                'late'
                ? Number(
                    value.lateMinutes
                  )
                : null,

            note:
              value.note
                ?.trim() ||
              '',

            schoolYear:
              classInfo?.schoolYear ||
              '2026-2027',

            updatedAt:
              serverTimestamp()
          };

          if (
            !existingAttendanceIds.has(
              attendanceId
            )
          ) {
            payload.createdAt =
              serverTimestamp();
          }

          batch.set(
            attendanceRef,
            payload,
            {
              merge: true
            }
          );
        }
      );

      await batch.commit();

      setExistingAttendanceIds(
        new Set(
          students.map(
            (student) =>
              [
                attendanceDate,
                safeId(classId),
                safeId(
                  selectedSubjectId
                ),
                safeId(
                  user.uid
                ),
                safeId(
                  student.id
                )
              ].join('__')
          )
        )
      );

      setAttendanceSuccess(
        `Présences enregistrées pour ${students.length} élève${students.length > 1 ? 's' : ''}.`
      );
    } catch (saveError) {
      console.error(
        'Erreur enregistrement présences :',
        saveError
      );

      setAttendanceError(
        saveError?.message ||
        'Impossible d’enregistrer les présences.'
      );
    } finally {
      setSavingAttendance(false);
    }
  }

  if (loading) {
    return (
      <TeacherShell activePage="classes">
        <div className="parent-empty large">Chargement de la classe…</div>
      </TeacherShell>
    );
  }

  return (
    <TeacherShell activePage="classes">
        {error ? (
          <section className="parent-card-v0">
            <h2>
              Accès impossible
            </h2>

            <p className="muted">
              {error}
            </p>

            <button
              type="button"
              className="button button-primary"
              onClick={() =>
                navigate(
                  '/enseignant'
                )
              }
            >
              Retour au tableau de bord
            </button>
          </section>
        ) : (
          <>
            <section className="parent-section-title teacher-content-heading-v1">
              <div>
                <p className="eyebrow">
                  MA CLASSE
                </p>

                <h1>
                  {className}
                </h1>

                <p className="muted">
                  {subjects.length > 0
                    ? subjects
                        .map(
                          (subject) =>
                            subject.name
                        )
                        .join(' • ')
                    : 'Aucune matière'}
                </p>
              </div>
            </section>

            {/* =====================================
                PARAMÈTRES DU RELEVÉ
            ===================================== */}

            <section
              className="parent-card-v0"
              style={{
                marginBottom:
                  '20px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems:
                    'center',
                  gap: '12px',
                  marginBottom:
                    '20px'
                }}
              >
                <CalendarDays
                  size={26}
                />

                <div>
                  <h2
                    style={{
                      margin: 0
                    }}
                  >
                    Présences
                  </h2>

                  <p
                    className="muted"
                    style={{
                      margin:
                        '4px 0 0'
                    }}
                  >
                    Sélectionnez la
                    date et la matière.
                  </p>
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '16px'
                }}
              >
                <label>
                  Date

                  <input
                    type="date"
                    value={
                      attendanceDate
                    }
                    onChange={(
                      event
                    ) =>
                      setAttendanceDate(
                        event.target
                          .value
                      )
                    }
                  />
                </label>

                <label>
                  Matière

                  <select
                    value={
                      selectedSubjectId
                    }
                    onChange={(
                      event
                    ) =>
                      setSelectedSubjectId(
                        event.target
                          .value
                      )
                    }
                    required
                  >
                    {subjects.length >
                      1 && (
                      <option value="">
                        Sélectionner
                        une matière
                      </option>
                    )}

                    {subjects.map(
                      (subject) => (
                        <option
                          key={
                            subject.id
                          }
                          value={
                            subject.id
                          }
                        >
                          {
                            subject.name
                          }
                        </option>
                      )
                    )}
                  </select>
                </label>
              </div>
            </section>

            {/* =====================================
                LISTE DES ÉLÈVES
            ===================================== */}

            <section className="parent-card-v0">
              <div
                style={{
                  display: 'flex',
                  alignItems:
                    'center',
                  gap: '12px',
                  marginBottom:
                    '20px'
                }}
              >
                <GraduationCap
                  size={28}
                />

                <div>
                  <h2
                    style={{
                      margin: 0
                    }}
                  >
                    Liste des élèves
                  </h2>

                  <p
                    className="muted"
                    style={{
                      margin:
                        '4px 0 0'
                    }}
                  >
                    {students.length}{' '}
                    élève
                    {students.length >
                    1
                      ? 's'
                      : ''}
                  </p>
                </div>
              </div>

              {loadingAttendance && (
                <p className="muted">
                  Chargement des
                  présences…
                </p>
              )}

              {!loadingAttendance &&
                students.length ===
                  0 && (
                  <p className="muted">
                    Aucun élève dans
                    cette classe.
                  </p>
                )}

              {!loadingAttendance &&
                students.length >
                  0 && (
                  <div
                    style={{
                      display:
                        'grid',
                      gap: '14px'
                    }}
                  >
                    {students.map(
                      (
                        student,
                        index
                      ) => {
                        const value =
                          attendance[
                            student.id
                          ] || {
                            status:
                              'present',
                            lateMinutes:
                              '',
                            note: ''
                          };

                        return (
                          <div
                            key={
                              student.id
                            }
                            style={{
                              border:
                                '1px solid #e5e7eb',
                              borderRadius:
                                '14px',
                              padding:
                                '16px'
                            }}
                          >
                            <div
                              style={{
                                display:
                                  'flex',
                                alignItems:
                                  'center',
                                gap:
                                  '12px'
                              }}
                            >
                              <div
                                style={{
                                  width:
                                    '42px',
                                  height:
                                    '42px',
                                  borderRadius:
                                    '50%',
                                  display:
                                    'grid',
                                  placeItems:
                                    'center',
                                  background:
                                    '#eef4ff'
                                }}
                              >
                                <UserRound
                                  size={
                                    21
                                  }
                                />
                              </div>

                              <div
                                style={{
                                  flex: 1
                                }}
                              >
                                <strong>
                                  {studentName(
                                    student
                                  )}
                                </strong>

                                <div className="muted">
                                  Élève{' '}
                                  {index +
                                    1}
                                </div>
                              </div>
                            </div>

                            {/* STATUS */}

                            <div
                              style={{
                                display:
                                  'flex',
                                flexWrap:
                                  'wrap',
                                gap:
                                  '8px',
                                marginTop:
                                  '16px'
                              }}
                            >
                              <button
                                type="button"
                                className={
                                  value.status ===
                                  'present'
                                    ? 'button button-primary'
                                    : 'button button-secondary'
                                }
                                onClick={() =>
                                  changeStatus(
                                    student.id,
                                    'present'
                                  )
                                }
                              >
                                <CheckCircle2
                                  size={
                                    16
                                  }
                                />
                                Présent
                              </button>

                              <button
                                type="button"
                                className={
                                  value.status ===
                                  'absent'
                                    ? 'button button-primary'
                                    : 'button button-secondary'
                                }
                                onClick={() =>
                                  changeStatus(
                                    student.id,
                                    'absent'
                                  )
                                }
                              >
                                <CircleX
                                  size={
                                    16
                                  }
                                />
                                Absent
                              </button>

                              <button
                                type="button"
                                className={
                                  value.status ===
                                  'late'
                                    ? 'button button-primary'
                                    : 'button button-secondary'
                                }
                                onClick={() =>
                                  changeStatus(
                                    student.id,
                                    'late'
                                  )
                                }
                              >
                                <Clock3
                                  size={
                                    16
                                  }
                                />
                                Retard
                              </button>
                            </div>

                            {/* RETARD */}

                            {value.status ===
                              'late' && (
                              <label
                                style={{
                                  display:
                                    'block',
                                  marginTop:
                                    '14px'
                                }}
                              >
                                Retard
                                (minutes)

                                <input
                                  type="number"
                                  min="1"
                                  max="240"
                                  value={
                                    value.lateMinutes
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateStudentAttendance(
                                      student.id,
                                      'lateMinutes',
                                      event
                                        .target
                                        .value
                                    )
                                  }
                                  placeholder="Exemple : 15"
                                />
                              </label>
                            )}

                            {/* REMARQUE */}

                            {(value.status ===
                              'absent' ||
                              value.status ===
                                'late') && (
                              <label
                                style={{
                                  display:
                                    'block',
                                  marginTop:
                                    '12px'
                                }}
                              >
                                Motif /
                                remarque

                                <input
                                  value={
                                    value.note
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateStudentAttendance(
                                      student.id,
                                      'note',
                                      event
                                        .target
                                        .value
                                    )
                                  }
                                  placeholder="Facultatif"
                                />
                              </label>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}

              {attendanceError && (
                <div
                  className="alert alert-error"
                  style={{
                    marginTop:
                      '20px'
                  }}
                >
                  {attendanceError}
                </div>
              )}

              {attendanceSuccess && (
                <div
                  style={{
                    marginTop:
                      '20px',
                    padding:
                      '14px',
                    borderRadius:
                      '12px',
                    background:
                      '#dcfce7'
                  }}
                >
                  {
                    attendanceSuccess
                  }
                </div>
              )}

              {students.length >
                0 && (
                <div
                  style={{
                    marginTop:
                      '22px',
                    display:
                      'flex',
                    justifyContent:
                      'flex-end'
                  }}
                >
                  <button
                    type="button"
                    className="button button-primary"
                    disabled={
                      savingAttendance ||
                      loadingAttendance ||
                      !selectedSubjectId
                    }
                    onClick={
                      saveAttendance
                    }
                  >
                    <Save
                      size={18}
                    />

                    {savingAttendance
                      ? 'Enregistrement…'
                      : 'Enregistrer les présences'}
                  </button>
                </div>
              )}
            </section>
          </>
        )}
    </TeacherShell>
  );
}
